import { FC, memo, useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ReplayIcon from "@mui/icons-material/Replay";
import { useQueryStates } from "@/contexts/Taxonomy/TaxonomyContext";
import { AccordionSection } from "../../../components/AccordionSection";
import { GeoApproachToggle } from "../../../components/GeoApproachToggle";
import { GeoCountryCheckboxList } from "../../../components/GeoCountryCheckboxList";
import { GeoRegionsSelector } from "../../../components/GeoRegionsSelector";
import { GeoStatesCheckboxList } from "../../../components/GeoStatesCheckboxList";
import {
  useCompanyFilterActions,
  useCompanyFilterOptions,
  useCompanyFilterSlice,
} from "../../../hooks";
import type { RegionId } from "../../../types";
import type { GeographyOptions } from "../../geography/options";
import {
  DEFAULT_REGIONS,
  buildUniqueSortedStates,
  toggleExcludedValue,
  toggleIncludedValue,
} from "../../../utils/geoSelection";

const US_COUNTRY_CODE = "US";
const EMPTY_COUNTRIES: Array<{ code: string; name: string }> = [];

type ClearTextButtonProps = {
  disabled: boolean;
  onClick: () => void;
};

const ClearTextButton: FC<ClearTextButtonProps> = memo(
  ({ disabled, onClick }) => {
    return (
      <Button
        size="small"
        variant="text"
        disabled={disabled}
        onClick={onClick}
        sx={{ textTransform: "none", minWidth: 48 }}
      >
        Clear
      </Button>
    );
  }
);

ClearTextButton.displayName = "ClearTextButton";

export const LocationSection: FC = memo(() => {
  const slice = useCompanyFilterSlice("intent");
  const { setSlice } = useCompanyFilterActions();
  const geographyOptions = useCompanyFilterOptions("geography") as
    | GeographyOptions
    | undefined;

  const [expanded, setExpanded] = useState(false);
  const [includeSearch, setIncludeSearch] = useState("");
  const [excludeSearch, setExcludeSearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");

  const regions = geographyOptions?.regions?.length
    ? geographyOptions.regions
    : DEFAULT_REGIONS;
  const countries = geographyOptions?.countries ?? EMPTY_COUNTRIES;

  const updateLocation = useCallback(
    (patch: Partial<typeof slice.location>) => {
      setSlice("intent", {
        ...slice,
        location: { ...slice.location, ...patch },
      });
    },
    [setSlice, slice]
  );

  const hasAnyLocationSelection =
    slice.location.countryAlpha2s.length > 0 ||
    slice.location.excludedCountryAlpha2s.length > 0 ||
    slice.location.regionNames.length > 0 ||
    slice.location.stateIds.length > 0;

  const handleClearAll = useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      updateLocation({
        countryAlpha2s: [],
        excludedCountryAlpha2s: [],
        regionNames: [],
        stateIds: [],
      });
      setIncludeSearch("");
      setExcludeSearch("");
      setStateSearch("");
    },
    [updateLocation]
  );

  const handleClearRegions = useCallback(() => {
    updateLocation({ regionNames: [] });
  }, [updateLocation]);

  const handleClearIncludedCountries = useCallback(() => {
    updateLocation({ countryAlpha2s: [] });
  }, [updateLocation]);

  const handleClearExcludedCountries = useCallback(() => {
    updateLocation({ excludedCountryAlpha2s: [] });
  }, [updateLocation]);

  const handleClearStates = useCallback(() => {
    updateLocation({ stateIds: [], countryAlpha2s: [] });
  }, [updateLocation]);

  const handleApproachChange = useCallback(
    (next: "regions" | "countries" | "states") => {
      if (next === slice.location.approach) return;

      if (next === "regions") {
        updateLocation({
          approach: next,
          countryAlpha2s: [],
          stateIds: [],
        });
        return;
      }

      if (next === "countries") {
        updateLocation({
          approach: next,
          regionNames: [],
          stateIds: [],
        });
        return;
      }

      updateLocation({
        approach: next,
        regionNames: [],
        excludedCountryAlpha2s: [],
        countryAlpha2s: [],
        stateIds: [],
      });
    },
    [slice.location.approach, updateLocation]
  );

  const handleRegionChange = useCallback(
    (regionId: RegionId, checked: boolean) => {
      const nextRegions = checked
        ? [...slice.location.regionNames, regionId]
        : slice.location.regionNames.filter((r) => r !== regionId);
      updateLocation({ regionNames: nextRegions });
    },
    [slice.location.regionNames, updateLocation]
  );

  const handleCountryChange = useCallback(
    (countryCode: string, checked: boolean) => {
      const next = toggleIncludedValue(
        slice.location.countryAlpha2s,
        slice.location.excludedCountryAlpha2s,
        countryCode,
        checked
      );

      updateLocation({
        countryAlpha2s: next.included,
        excludedCountryAlpha2s: next.excluded,
      });
    },
    [
      slice.location.countryAlpha2s,
      slice.location.excludedCountryAlpha2s,
      updateLocation,
    ]
  );

  const handleExcludeCountryChange = useCallback(
    (countryCode: string, checked: boolean) => {
      const next = toggleExcludedValue(
        slice.location.countryAlpha2s,
        slice.location.excludedCountryAlpha2s,
        countryCode,
        checked
      );

      updateLocation({
        countryAlpha2s: next.included,
        excludedCountryAlpha2s: next.excluded,
      });
    },
    [
      slice.location.countryAlpha2s,
      slice.location.excludedCountryAlpha2s,
      updateLocation,
    ]
  );

  const handleStateToggle = useCallback(
    (stateId: string) => {
      const nextStates = slice.location.stateIds.includes(stateId)
        ? slice.location.stateIds.filter((id) => id !== stateId)
        : [...slice.location.stateIds, stateId];

      updateLocation({
        stateIds: nextStates,
        countryAlpha2s: nextStates.length > 0 ? [US_COUNTRY_CODE] : [],
      });
    },
    [slice.location.stateIds, updateLocation]
  );

  const { data: statesData, loading: statesLoading } = useQueryStates({
    countryAlpha2: US_COUNTRY_CODE,
  });

  const statesForUs = useMemo(
    () => buildUniqueSortedStates(statesData),
    [statesData]
  );

  return (
    <AccordionSection
      title="Location"
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
      action={
        hasAnyLocationSelection ? (
          <Tooltip title="Reset">
            <IconButton
              size="small"
              aria-label="Reset Location"
              onClick={handleClearAll}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null
      }
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Scope
          </Typography>
          <GeoApproachToggle
            value={slice.location.approach}
            onChange={handleApproachChange}
          />
        </Box>

        <Divider />

        <Box>
          {slice.location.approach === "regions" && (
            <Stack spacing={1.5}>
              <GeoRegionsSelector
                regions={regions}
                selectedRegionIds={slice.location.regionNames as RegionId[]}
                onToggle={handleRegionChange}
                action={
                  <ClearTextButton
                    disabled={slice.location.regionNames.length === 0}
                    onClick={handleClearRegions}
                  />
                }
              />

              <GeoCountryCheckboxList
                title="Exclude Countries (optional)"
                countries={countries}
                searchValue={excludeSearch}
                onSearchChange={setExcludeSearch}
                selectedCodes={slice.location.excludedCountryAlpha2s}
                onToggle={handleExcludeCountryChange}
                maxHeight={220}
                searchInputId="intent-location-countries-exclude-search-input"
                action={
                  <ClearTextButton
                    disabled={
                      slice.location.excludedCountryAlpha2s.length === 0
                    }
                    onClick={handleClearExcludedCountries}
                  />
                }
              />
            </Stack>
          )}

          {slice.location.approach === "countries" && (
            <Stack spacing={1.5}>
              <GeoCountryCheckboxList
                title="Include Countries"
                countries={countries}
                searchValue={includeSearch}
                onSearchChange={setIncludeSearch}
                selectedCodes={slice.location.countryAlpha2s}
                filterOutCodes={slice.location.excludedCountryAlpha2s}
                onToggle={handleCountryChange}
                maxHeight={220}
                searchInputId="intent-location-countries-include-search-input"
                action={
                  <ClearTextButton
                    disabled={slice.location.countryAlpha2s.length === 0}
                    onClick={handleClearIncludedCountries}
                  />
                }
              />

              <GeoCountryCheckboxList
                title="Exclude Countries (optional)"
                countries={countries}
                searchValue={excludeSearch}
                onSearchChange={setExcludeSearch}
                selectedCodes={slice.location.excludedCountryAlpha2s}
                filterOutCodes={slice.location.countryAlpha2s}
                onToggle={handleExcludeCountryChange}
                maxHeight={220}
                searchInputId="intent-location-countries-exclude-search-input"
                action={
                  <ClearTextButton
                    disabled={
                      slice.location.excludedCountryAlpha2s.length === 0
                    }
                    onClick={handleClearExcludedCountries}
                  />
                }
              />
            </Stack>
          )}

          {slice.location.approach === "states" && (
            <Stack spacing={1.5}>
              <GeoStatesCheckboxList
                title="States (US only)"
                states={statesForUs}
                selectedStateIds={slice.location.stateIds}
                onToggle={handleStateToggle}
                searchValue={stateSearch}
                onSearchChange={setStateSearch}
                loading={statesLoading}
                emptyMessage="No states available."
                noResultsMessage="No states found."
                maxHeight={250}
                searchInputId="intent-location-states-search-input"
                action={
                  <ClearTextButton
                    disabled={slice.location.stateIds.length === 0}
                    onClick={handleClearStates}
                  />
                }
              />
            </Stack>
          )}
        </Box>
      </Stack>
    </AccordionSection>
  );
});

LocationSection.displayName = "LocationSection";
